import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { PageHeader } from '@/components/core';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createLeaveCategoryTableColumns } from '../../components/leave-category-table-columns/leave-category-table-columns';
import { LeaveCategoryFormDialog } from '../../components/leave-category-form-dialog/leave-category-form-dialog';
import { useDeleteLeaveCategory, useGetLeaveCategories } from '../../hooks/use-leave-categories';
import { DEFAULT_LEAVE_CATEGORIES_ORDER } from '../../graphql/queries';
import { LeaveCategoriesPagingResult, LeaveCategory } from '../../types/leave-category.types';

export const LeaveBuilderPage = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.LEAVE_CATEGORY.CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.LEAVE_CATEGORY.UPDATE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.LEAVE_CATEGORY.DELETE);

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_LEAVE_CATEGORIES_ORDER,
  });
  const [formState, setFormState] = useState<{
    open: boolean;
    leaveCategory: LeaveCategory | null;
  }>({
    open: false,
    leaveCategory: null,
  });
  const [deleteState, setDeleteState] = useState<{
    open: boolean;
    leaveCategory: LeaveCategory | null;
  }>({
    open: false,
    leaveCategory: null,
  });

  const { data, isLoading, error } = useGetLeaveCategories({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { LeaveCategorys?: LeaveCategoriesPagingResult })?.LeaveCategorys;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteLeaveCategory, isPending: isDeleting } = useDeleteLeaveCategory();

  const handleDeleteConfirm = async () => {
    if (!deleteState.leaveCategory) return;
    try {
      await deleteLeaveCategory(deleteState.leaveCategory);
      setDeleteState({ open: false, leaveCategory: null });
    } catch {
      // The refusal (in use, a system category) is toasted by the mutation's global error handler.
    }
  };

  const columns = createLeaveCategoryTableColumns({
    t,
    canEdit: canUpdate,
    canDelete,
    onEdit: (leaveCategory) => setFormState({ open: true, leaveCategory }),
    onDelete: (leaveCategory) => setDeleteState({ open: true, leaveCategory }),
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('LEAVE_BUILDER')}
        actions={
          canCreate
            ? [
                {
                  label: t('CREATE_LEAVE_TYPE'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setFormState({ open: true, leaveCategory: null }),
                },
              ]
            : []
        }
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <LeaveCategoryFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, leaveCategory: open ? prev.leaveCategory : null }))
        }
        leaveCategory={formState.leaveCategory}
      />

      <ConfirmationModal
        open={deleteState.open}
        onOpenChange={(open) =>
          setDeleteState({ open, leaveCategory: open ? deleteState.leaveCategory : null })
        }
        title={t('DELETE_LEAVE_TYPE')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
